import React from 'react'
import styled from 'styled-components'
import { noLog as log } from '../../shared/Utils'
import { Localized } from '@fluent/react'

import {
  AudioIssuePrompt,
  PreviewImage,
  PreviewContainer,
  PreviewLink,
  PreviewBody,
  PreviewTitle,
  PreviewDescription,
  ExtensionBox
} from './layout'

import { Block, Button, H1, P, Text } from '@mote/ui'
import { CWS_URL, WEB_STORAGE_PREFIX_KEYS } from '@mote/helpers/constants'

import { MoteLandingLayout } from './MoteLandingLayout'
import { Emoji } from '@mote/presentation'
import { isIOS, useLocalStoragePermission } from '@mote/helpers/browser'

import { ReactComponent as AppleStoreSVG } from './apple-store.svg'
import { ChromeIcon } from '@mote/assets/icons'
import { isExtensionSupported } from '@mote/helpers/extension'
import { MoteCardRichWrapper } from '@mote/presentation/src/moteCard/rich/MoteCardRichWrapper'
import {
  useMoteListenActivityMutation,
  useMoteReactUnreactMutation,
  useTrackMutation
} from '@mote/api'

const PLAYER_ID = 'audio-player'

const extensionSupported = isExtensionSupported()
const isIOSDevice = isIOS()

export const Mote = ({
  stripLayout = false,
  mote,
  hasRedirectionPermissions,
  extensionInstalled,
  onRequestRedirectionPermissions
}) => {
  const { mutate: track } = useTrackMutation()

  const { mutate: emojiReact } = useMoteReactUnreactMutation({
    moteId: mote.parentMote.moteId
  })

  const { mutate: listenActivity } = useMoteListenActivityMutation({
    moteId: mote.parentMote.moteId
  })

  const [myReactions, setMyReactions] = React.useState(
    mote.parentMote.myReactions
  )
  const [hasAudioListeners, setHasAudioListeners] = React.useState(false)
  const [audioIssues, setAudioIssues] = React.useState(false)

  const everPlayedRef = React.useRef(false)
  const canAccessLocalStorage = useLocalStoragePermission()

  const playTracking = React.useCallback(async () => {
    await track({
      variables: {
        event: 'Mote Landing Page Audio Started or Restarted',
        content_asset_id: mote.parentMote.moteId,
        content_rich: mote.canRedirect,
        creator_cohort: mote.parentMote.labs?.labsCohort
      }
    })

    if (!everPlayedRef.current) {
      await listenActivity()
      // needs to be a ref to avoid re-rendering
      // as it causes audio play issues on initial play
      everPlayedRef.current = true
    }
  }, [everPlayedRef, mote])

  const pauseTracking = React.useCallback(async () => {
    await track({
      variables: {
        event: 'Mote Landing Page Audio Paused or Ended',
        content_asset_id: mote.parentMote.moteId,
        content_rich: mote.canRedirect,
        creator_cohort: mote.parentMote.labs?.labsCohort
      }
    })
  }, [mote])

  const onEnd = React.useCallback(() => {
    track({
      variables: {
        event: 'Mote Play Audio Ended',
        content_asset_id: mote.parentMote.moteId,
        creator_cohort: mote.parentMote.labs?.labsCohort
      }
    })
  }, [mote])

  const shouldEnableReactions = React.useCallback(() => {
    if (!mote) return false

    const result =
      mote.parentMote.availableReactions?.length > 0 ||
      mote.parentMote.availableUnreactions?.length > 0
    return result
  }, [mote])

  const toggleEmoji = React.useCallback(
    reaction => {
      const isEnabled = !myReactions.includes(reaction)

      if (!isEnabled) {
        setMyReactions(myReactions.filter(item => item !== reaction))
      } else {
        setMyReactions([reaction])
        track({
          variables: {
            event: 'Mote Reaction',
            reaction,
            author_asset_id: mote.parentMote.moteId,
            author_user_id: mote.parentMote.createdByUserId,
            creator_cohort: mote.parentMote.labs?.labsCohort
          }
        })
      }

      emojiReact({
        variables: {
          isEnabled,
          reaction
        }
      }).catch(console.error)
    },
    [myReactions, mote]
  )

  const onEmojiHandler = React.useCallback(
    emoji => {
      if (shouldEnableReactions()) return toggleEmoji(emoji)

      return null
    },
    [shouldEnableReactions, toggleEmoji]
  )

  const handlePlayAgainTracking = React.useCallback(() => {
    track({
      variables: {
        event: 'Mote Replayed',
        creator_cohort: mote.parentMote.labs?.labsCohort
      }
    })
  }, [mote])

  const handleShowFullTranscriptTracking = React.useCallback(() => {
    track({
      variables: {
        event: 'Mote Landing Page Full Transcript Viewed',
        creator_cohort: mote.parentMote.labs?.labsCohort
      }
    })
  }, [mote])

  const renderCard = React.useMemo(() => {
    return (
      <Block justify='center' align='center' spreadX>
        <MoteCardRichWrapper
          transcript={mote.parentMote.transcriptDisplay}
          createdByName={mote.parentMote.createdByName}
          createdByAvatar={mote.parentMote.createdByAvatar}
          createdByUserId={mote.parentMote.createdByUserId}
          createdByGreeting={mote.parentMote.createdByGreeting}
          replyMotes={mote.replyMotes}
          audioUrl={mote.parentMote.playbackLocation}
          duration={mote.parentMote.duration}
          theme={mote.parentMote.theme}
          onPlay={playTracking}
          onPause={pauseTracking}
          onEnd={onEnd}
          niceTitle={mote.parentMote.niceTitle}
          onEmojiSelected={toggleEmoji}
          shouldEnableReactions={shouldEnableReactions()}
          enableReplyButton
          isCreator={
            canAccessLocalStorage &&
            window.localStorage.getItem(`${WEB_STORAGE_PREFIX_KEYS}userId`) ===
              mote.parentMote.createdByUserId
          }
          onPlayAgain={handlePlayAgainTracking}
          myReactions={myReactions}
          waveform={mote.parentMote.waveform}
          waveformFrameMilliseconds={mote.parentMote.waveformFrameMilliseconds}
          onShowFullTranscript={handleShowFullTranscriptTracking}
          moteId={mote.parentMote.moteId}
          isLandingPage
        />
        <AudioIssuePrompt onClick={() => setAudioIssues(true)}>
          <Localized id='lpm--cant-hear-any-audio-click'>
            Can't hear any audio? Click here!
          </Localized>
        </AudioIssuePrompt>

        {audioIssues && (
          <audio
            id={PLAYER_ID}
            controls
            style={{
              outline: 'none',
              marginTop: '12px',
              width: '100%',
              maxWidth: '400px'
            }}
            autoPlay={true}
            src={mote.parentMote.playbackLocation}
          />
        )}
      </Block>
    )
  }, [
    playTracking,
    pauseTracking,
    audioIssues,
    mote,
    myReactions,
    onEnd,
    onEmojiHandler,
    shouldEnableReactions,
    handlePlayAgainTracking
  ])

  React.useEffect(() => {
    if (!mote) return
    if (!mote.parentMote.playbackLocation) return

    const player = document.getElementById(PLAYER_ID)
    if (!player) {
      log('We could not find an audio player')
    } else if (!hasAudioListeners) {
      setHasAudioListeners(true)
      log('yay, we have mapped listeners to the audio player!!!')
      player.addEventListener('play', playTracking)
      player.addEventListener('pause', pauseTracking)
    } else {
      log('we have already assigned something')
    }

    if (canAccessLocalStorage && mote.parentMote.createdByUserId) {
      log('Setting referrer to: ', mote.parentMote.createdByUserId)
      window.localStorage.setItem(
        'mote_referrer_user_id',
        mote.parentMote.createdByUserId
      )
    }
  }, [
    mote,
    playTracking,
    pauseTracking,
    hasAudioListeners,
    canAccessLocalStorage
  ])

  if (stripLayout) return <>{renderCard}</>

  return (
    <MoteLandingLayout
      hero={
        <Container align='center' justify='center' gap={40}>
          <Localized
            id='lpm--moter-left-voice-note-for'
            vars={{ moter: mote.parentMote.createdByName }}
          >
            <StyledH1 alignment='center' fontWeight={500}>
              {'{$moter} left a voice note for you'}
            </StyledH1>
          </Localized>

          <Block maxWidth={460} spreadX>
            {renderCard}
          </Block>
        </Container>
      }
    >
      {mote.parentMote.canRedirect && mote.parentMote.canLink && (
        <Block margin='0 0 30px 0'>
          <Text
            fontSize={28}
            fontWeight={400}
            lineHeight={'120%'}
            alignment='center'
            color='#232323'
          >
            {mote.parentMote.createdByName} left you this voice note on a
            webpage
            <Emoji label='point'>👇</Emoji> go check it out!
          </Text>

          <PreviewContainer>
            {mote.parentMote.containingURLHeroImage && (
              <PreviewImage src={mote.parentMote.containingURLHeroImage} />
            )}
            <PreviewBody>
              <PreviewLink href={mote.parentMote.containingURL}>
                {mote.parentMote.containingURL}
              </PreviewLink>
              <PreviewTitle>
                {mote.parentMote.containingURLTitle ||
                  'No linked content title found!'}
              </PreviewTitle>
              <PreviewDescription>
                {mote.parentMote.containingURLDescription ||
                  'No linked content description found!'}
              </PreviewDescription>
            </PreviewBody>
          </PreviewContainer>
        </Block>
      )}

      {isIOSDevice && (
        <ExtensionBox>
          <P color='white'>
            Install the Mote iOS app to create your own voice notes.
          </P>
          <Block layout='horizontal' justify='center'>
            <a
              href='https://apps.apple.com/us/app/mote/id1568351949'
              target='_new'
            >
              <AppleStoreSVG />
            </a>
          </Block>
        </ExtensionBox>
      )}

      {!extensionInstalled && !isIOSDevice && (
        <ExtensionBox>
          <P color='white'>
            <Localized
              id={
                mote.canRedirect
                  ? 'lpm--add-mote-chrome-extension-any-webpage'
                  : 'lpm--add-the-mote-chrome-extension'
              }
            >
              Add the mote Chrome extension to create your own voice notes.
            </Localized>
          </P>
          <Block>
            <Button
              data-testid='add-to-chrome-button'
              variant='secondary'
              icon={<ChromeIcon />}
              text={
                <Localized
                  id={
                    extensionSupported
                      ? 'lpm--add-to-chrome'
                      : 'lpm--available-on-chrome'
                  }
                >
                  {extensionSupported ? 'Add to Chrome' : 'Available on Chrome'}
                </Localized>
              }
              onClick={() => {
                track({
                  variables: {
                    event: 'Install Extension Clicked',
                    referrer_user_id: window.localStorage.getItem(
                      'mote_referrer_user_id'
                    )
                  }
                })
                window.open(CWS_URL, '_new')
              }}
            />
          </Block>
        </ExtensionBox>
      )}

      {extensionInstalled && !isIOSDevice && !hasRedirectionPermissions && (
        <ExtensionBox>
          <P color='white'>
            <Emoji label='wave'>👋</Emoji> - to use Mote in apps like Gmail, and
            around the web, you’ll need to grant additional permissions.
          </P>
          <Button
            variant='secondary'
            text='Grant Permissions'
            onClick={onRequestRedirectionPermissions}
          />
        </ExtensionBox>
      )}
    </MoteLandingLayout>
  )
}

const Container = styled(Block)`
  margin: 40px 0;
  width: 100%;

  @media (max-width: 767px) {
    margin: 20px 0;
  }
`

const StyledH1 = styled(H1)`
  font-size: 28px;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`
